<template>
  <b-modal ref="modal" id="addIntegrationModal" hide-footer hide-header body-class="p-0">
    <div class="modal-card card">
      <div class="card-header">
        <div class="row align-items-center">
          <div class="col">
            <h4 class="card-header-title" id="exampleModalCenterTitle">
              {{ isConnectedModel ? 'Update' : 'Connect' }}
              {{ integration.displayName || integration.name }}
            </h4>
          </div>
          <div class="col-auto">
            <button @click="close" type="button" class="close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>
      </div>
      <div class="card-body" style="max-height: 650px">
        <div class="row">
          <!-- Simphony Pre-Authorization: show required fields before OAuth exists -->
          <div class="col-12 mb-4" v-if="isSimphonyPreAuth">
            <div class="form-group">
              <label for="simphony-oauthHostDomain" class="mb-1">OAuth Host Domain</label>
              <input
                id="simphony-oauthHostDomain"
                :ref="'simphony-oauthHostDomain'"
                :value="credentials.oauthHostDomain || ''"
                @input="updateCredentials('oauthHostDomain', $event.target.value)"
                placeholder="https://..."
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="simphony-stsGen2HostDomain" class="mb-1">STS Gen 2 Host Domain</label>
              <input
                id="simphony-stsGen2HostDomain"
                :ref="'simphony-stsGen2HostDomain'"
                :value="credentials.stsGen2HostDomain || ''"
                @input="updateCredentials('stsGen2HostDomain', $event.target.value)"
                placeholder="https://..."
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="simphony-clientId" class="mb-1">Client ID</label>
              <input
                id="simphony-clientId"
                :ref="'simphony-clientId'"
                :value="credentials.clientId || ''"
                @input="updateCredentials('clientId', $event.target.value)"
                placeholder="Client ID"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="simphony-orgShortName" class="mb-1">Org Short Name</label>
              <input
                id="simphony-orgShortName"
                :ref="'simphony-orgShortName'"
                :value="credentials.orgShortName || ''"
                @input="updateCredentials('orgShortName', $event.target.value)"
                placeholder="Org Short Name"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="simphony-username" class="mb-1">API UserName</label>
              <input
                id="simphony-username"
                :ref="'simphony-username'"
                :value="credentials.username || ''"
                @input="updateCredentials('username', $event.target.value)"
                placeholder="Username"
                class="form-control"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="simphony-password" class="mb-1">API Password</label>
              <input
                id="simphony-password"
                :ref="'simphony-password'"
                :value="credentials.password || ''"
                @input="updateCredentials('password', $event.target.value)"
                placeholder="Password"
                class="form-control"
                type="password"
              />
            </div>
          </div>
          <div class="col-12 mb-4" v-if="!loadingIntegration && !needsOauth && !isSimphonyPreAuth">
            <div class="form-group" v-if="isSpotOnApiProvider && hasValidSpotOnBlid">
              <label for="spoton-blid" class="mb-1">SpotOn Location ID</label>
              <input
                id="spoton-blid"
                readonly
                :value="spotOnBlid"
                class="form-control bg-light"
                type="text"
              />
            </div>
            <div class="form-group" v-if="isSpotOnApiProvider && !hasValidSpotOnBlid">
              <label for="spoton-blid-entry" class="mb-1">SpotOn BLID</label>
              <input
                id="spoton-blid-entry"
                :value="spotOnBlidInput"
                @input="updateSpotOnBlidInput($event.target.value)"
                :readonly="verifyingSpotOnBlid"
                placeholder="BL-XXXX-XXXX-XXXX"
                class="form-control"
                :class="{ 'is-invalid': spotOnBlidError }"
                type="text"
              />
              <small class="form-text text-muted"
                >Enter the SpotOn Business Location ID for this location.</small
              >
              <div class="invalid-feedback" v-if="spotOnBlidError">{{ spotOnBlidError }}</div>
            </div>
            <div v-if="integration.isOrg" class="form-group d-flex">
              <label class="">{{ integration.isOrg.label }}</label>
              <div class="custom-control custom-checkbox d-flex ml-3">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="integration.isOrg.name"
                  v-model="credentials.isOrg"
                />
                <label class="custom-control-label" :for="integration.isOrg.name"></label>
              </div>
            </div>
            <div class="form-group" v-for="input in integration.clipboards" :key="input.name">
              <label :for="input.name" class="mb-1">{{ input.label }}</label>
              <div class="input-group input-group-merge">
                <input
                  readonly
                  type="text"
                  :id="input.name"
                  :ref="input.name"
                  :placeholder="input.label"
                  :value="
                    credentials.isOrg
                      ? input.urlWithOrgId(orgId)
                      : input.urlWithCompanyId(companyId)
                  "
                  class="form-control form-control-appended bg-light"
                />
                <div class="input-group-append">
                  <div class="input-group-text p-0 bg-light">
                    <button
                      class="btn btn-light border-0"
                      aria-label="copy to clipboard"
                      v-clipboard:copy="
                        credentials.isOrg
                          ? input.urlWithOrgId(orgId)
                          : input.urlWithCompanyId(companyId)
                      "
                      v-clipboard:success="onCopyToClipboard"
                    >
                      <span class="fe fe-clipboard"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group" v-for="output in integrationOutputs" :key="output.name">
              <label :for="output.name" class="mb-1">{{ output.label }}</label>
              <div class="input-group input-group-merge" v-if="output.type === 'clipboard'">
                <input
                  readonly
                  type="text"
                  :id="output.name"
                  :ref="output.name"
                  :placeholder="output.label"
                  :value="getOutputValue(output)"
                  class="form-control form-control-appended bg-light"
                />
                <div class="input-group-append">
                  <div class="input-group-text p-0 bg-light">
                    <button
                      class="btn btn-light border-0"
                      aria-label="copy to clipboard"
                      v-clipboard:copy="getOutputValue(output)"
                      v-clipboard:success="onCopyToClipboard"
                    >
                      <span class="fe fe-clipboard"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group" v-if="!needsOauth && usesNamedOauthAccounts">
              <div class="row">
                <div class="col-md-6">
                  <input
                    type="text"
                    class="form-control"
                    v-model="integrationNickname"
                    placeholder="Enter OAuth Account Name"
                  />
                </div>
                <div class="col-md-6">
                  <button
                    class="btn btn-success w-100"
                    @click="handleAddOauthAccount"
                    :disabled="!integrationNickname"
                  >
                    Add New Account
                  </button>
                </div>
              </div>
            </div>

            <div
              class="form-group"
              v-if="
                this.needsOauth === false && this.usesNamedOauthAccounts && oAuthAccounts.length > 0
              "
            >
              <label for="oauth-select" class="mb-1">{{
                `${integration.displayName} Accounts`
              }}</label>
              <select
                name="oauth-select"
                class="form-control"
                v-model="oAuthName"
                @input="selectOauth($event.target.value)"
              >
                <option></option>
                <option
                  v-for="oAuthAccount of oAuthAccounts"
                  :key="oAuthAccount.name"
                  :value="oAuthAccount.name == 'default' ? null : oAuthAccount.name"
                >
                  {{ oAuthAccount.name }}
                </option>
              </select>
            </div>

            <div
              class="form-group"
              v-if="connectedIntegration && integration && showLocationSelect"
            >
              <label for="location-select" class="mb-1">{{
                `${integration.displayName} Location`
              }}</label>
              <select
                name="location-select"
                class="form-control"
                :value="connectedIntegrationLocation ? connectedIntegrationLocation.id : undefined"
                @input="selectLocation($event.target.value)"
              >
                <option></option>
                <option
                  v-for="locationOption of externalLocations"
                  :key="locationOption._key"
                  :value="locationOption._key"
                >
                  {{ locationOption.optionName }}
                </option>
              </select>
            </div>
            <div class="form-group" v-for="input in integrationInputs" :key="input.name">
              <label :for="input.name" class="mb-1">{{ input.label }}</label>
              <!-- this seems wrong to use updateCredentials here instead of updateField -->
              <!-- but I'm afraid of breaking other stuff... :/ -->
              <input
                :id="input.name"
                :ref="input.name"
                :value="getInputValue(input)"
                @input="updateCredentials(input.name, $event.target.value)"
                :placeholder="input.label"
                class="form-control"
                :type="input.type"
              />
            </div>
            <template v-if="!awaitingLegacyCredentials">
              <div class="form-group" v-for="input in oldIntegrationInputs" :key="input.name">
                <label :for="input.name" class="mb-1">{{ input.label }}</label>
                <input
                  :id="input.name"
                  :ref="input.name"
                  :value="getCredentialValue(input.name)"
                  @input="updateCredentials(input.name, $event.target.value)"
                  :placeholder="input.label"
                  class="form-control"
                  :type="input.type"
                />
              </div>
            </template>
            <div class="form-group" v-for="input in checkboxInputs" :key="input.name">
              <label class="mb-1">{{ input.label }}</label>
              <div class="row">
                <div class="col-auto">
                  <!-- Switch -->
                  <div class="custom-control custom-checkbox-toggle">
                    <input
                      class="custom-control-input"
                      :id="input.name"
                      :ref="input.name"
                      @change="updateField(input.name, $event.target.checked)"
                      :checked="getInputValue(input)"
                      type="checkbox"
                    />
                    <label class="custom-control-label" :for="input.name"></label>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="survey-delay" class="mb-1">Survey Delay (Minutes)</label>
              <span
                style="cursor: pointer"
                class="ml-2"
                v-b-tooltip.hover="
                  `Delay sending the survey invitation by the specified number of minutes after an order is recorded.`
                "
              >
                <i class="fe fe-info"></i>
              </span>
              <input
                id="survey-delay"
                ref="survey-delay"
                placeholder="30"
                class="form-control"
                type="number"
                min="1"
                step="1"
                v-model.number="integration.surveyDelay"
                @input="validatePositiveInteger('surveyDelay')"
              />
            </div>

            <div class="form-group">
              <label for="survey-throttle" class="mb-1">Survey Throttle (Days)</label>
              <span
                style="cursor: pointer"
                class="ml-2"
                v-b-tooltip.hover="`Ensure a customer receives at most one survey every X days.`"
              >
                <i class="fe fe-info"></i>
              </span>
              <input
                id="survey-throttle"
                ref="survey-throttle"
                class="form-control"
                type="number"
                min="1"
                step="1"
                v-model.number="integration.surveyThrottle"
                @input="validatePositiveInteger('surveyThrottle')"
              />
            </div>

            <div class="form-group">
              <label for="survey-order-count-throttle" class="mb-1"
                >Survey Order Count Throttle</label
              >
              <span
                style="cursor: pointer"
                class="ml-2"
                v-b-tooltip.hover="
                  `Ensure an integration only sends surveys at most one survey per X orders that are processed.`
                "
              >
                <i class="fe fe-info"></i>
              </span>
              <input
                id="survey-order-count-throttle"
                ref="survey-order-count-throttle"
                class="form-control"
                type="number"
                min="1"
                step="1"
                v-model.number="integration.surveyOrderCountThrottle"
                @input="validatePositiveInteger('surveyOrderCountThrottle')"
              />
            </div>
          </div>
          <div class="col-12" v-if="connectedIntegration && needsOauth && !isSimphonyPreAuth">
            <button
              class="btn col-12"
              style="background-color: #ff4450"
              @click="handleAuthorizeClick"
              :disabled="isLoading || loadingIntegration || !hasAuthorizationLink"
              :class="{ 'is-loading': isLoading || loadingIntegration }"
            >
              {{ `Authorize Ovation via ${integration.displayName}` }}
            </button>
            <div class="mt-2" v-if="oauthLinkDegraded">
              <div class="alert alert-danger mb-2" role="alert">
                Failed to load the authorization link for {{ integration.displayName }}. Please try
                again.
              </div>
              <button class="btn btn-outline-secondary col-12" @click="retryAuthorizationLink">
                Retry
              </button>
            </div>
          </div>
          <!-- Simphony Pre-Authorization authorize button (disabled until required fields filled) -->
          <div class="col-12" v-if="isSimphonyPreAuth">
            <button
              class="btn col-12"
              style="background-color: #ff4450"
              @click="handleSimphonyAuthorize()"
              :disabled="!isSimphonyFormComplete || isLoading"
            >
              {{ `Authorize Ovation via ${integration.displayName}` }}
            </button>
          </div>
          <div class="col-12" v-if="!needsOauth && !isSimphonyPreAuth">
            <button
              @click="isConnectedModel ? update() : create()"
              :disabled="
                isLoading ||
                !isFormValid ||
                addingIntegration ||
                loadingIntegration ||
                verifyingSpotOnBlid ||
                awaitingLegacyCredentials
              "
              class="btn btn-primary col-12"
              :class="{
                'is-loading':
                  isLoading ||
                  addingIntegration ||
                  loadingIntegration ||
                  verifyingSpotOnBlid ||
                  awaitingLegacyCredentials
              }"
            >
              {{ isConnectedModel ? 'Update' : 'Connect' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex'
import Config from '@/config'
import { spotOnBlidErrorMessage } from '@/lib/spoton-blid-error-message'
const IntegrationModule = createNamespacedHelpers('integration')
const CompanyModule = createNamespacedHelpers('company')
const IntegrationProviderModule = createNamespacedHelpers('integrationProvider')

// OAuth providers whose external location objects carry the merchant/company
// id under a provider-specific field name. Providers absent here simply skip
// the externalCompanyId mapping (see selectLocation()).
const OAUTH_EXTERNAL_COMPANY_ID_FIELDS = {
  square: 'merchant_id'
}

// Providers whose OAuth grant supports multiple named accounts per company
// (company.integrationDetails[provider].oauthAccounts, backed by the generic
// POST /company/add-oauth handshake). Other usesOauth providers authorize a
// single default grant and must not surface this UI. Add a provider here
// only once its backend handshake is confirmed to route through the generic
// oauthAccounts path.
const NAMED_OAUTH_ACCOUNT_PROVIDERS = ['square']

// spoton-api requires a SpotOn Business Location ID stamped onto
// Location.externalIdentifiers[key='spotOnLocationId'] before it can be
// enabled; the poller silently skips locations without one.
const SPOTON_BLID_PATTERN = /^BL-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/

export default {
  name: 'AddIntegrationModal',
  props: ['location'],
  components: {},
  data: () => ({
    dynamicFields: {},
    isLoading: false,
    integration: {},
    credentials: {},
    connectedIntegration: null,
    connectedIntegrationLocation: null,
    externalLocations: [],
    showLocationSelect: false,
    oAuthName: '',
    integrationNickname: '',
    isLoadingLocations: false,
    createdIntegrationId: null,
    hydratingIntegrationId: null,
    legacyHydrationState: 'idle',
    oauthLinkRetried: false,
    oauthLinkDegraded: false,
    spotOnBlidInput: '',
    spotOnBlidError: null,
    verifyingSpotOnBlid: false
  }),
  computed: {
    ...mapGetters(['selectIsLoading']),
    ...CompanyModule.mapGetters(['activeCompany']),
    ...IntegrationProviderModule.mapGetters(['selectIntegrationProviderById']),
    needsOauth() {
      return (
        this.connectedIntegration &&
        this.connectedIntegration.oauth &&
        !this.connectedIntegration.oauth.authorized &&
        !!this.integration?.usesOauth
      )
    },
    hasAuthorizationLink() {
      return !!this.connectedIntegration?.oauth?.authorizationLink
    },
    usesNamedOauthAccounts() {
      return (
        !!this.integration?.usesOauth &&
        NAMED_OAUTH_ACCOUNT_PROVIDERS.includes(this.integration.name)
      )
    },
    integrationOutputs() {
      return this.integration ? this.integration.outputs : []
    },
    checkboxInputs() {
      return this.integration?.inputs?.filter(({ type }) => type === 'checkbox')
    },
    /**
     * @deprecated
     */
    integrationInputs() {
      return this.connectedIntegration && this.connectedIntegration.integrationProvider
        ? this.integration.inputs?.filter(({ type }) => type !== 'checkbox')
        : []
    },
    /**
     * @deprecated
     */
    oldIntegrationInputs() {
      return this.connectedIntegration && this.connectedIntegration.integrationProvider
        ? []
        : this.integration.inputs?.filter(({ type }) => type !== 'checkbox')
    },
    addingIntegration() {
      return this.selectIsLoading([
        `ADD_INTEGRATION_${
          this.integration && this.integration.type ? this.integration.type : null
        }`
      ])
    },
    loadingIntegration() {
      return this.selectIsLoading([`GET_INTEGRATION_${this.connectedIntegration?._id}`])
    },
    orgId() {
      return 'company' in this.location ? this.location.company.organization : ''
    },
    companyId() {
      return 'company' in this.location ? this.location.company._id : ''
    },
    oAuthAccounts() {
      const providerDetails = this.location.company.integrationDetails[this.integration.name]
      let accountsArray = []
      if (
        providerDetails &&
        providerDetails.oauthAccounts &&
        providerDetails.oauthAccounts.length > 0
      ) {
        accountsArray = [
          { name: 'default', oauth: providerDetails.oauth },
          ...providerDetails.oauthAccounts
        ]
      }
      return accountsArray
    },
    isSpotOnApiProvider() {
      return this.integration?.name === 'spoton-api'
    },
    spotOnBlid() {
      const entry = (this.location?.externalIdentifiers || []).find(
        ({ key }) => key === 'spotOnLocationId'
      )
      return entry ? entry.value : null
    },
    hasValidSpotOnBlid() {
      return !!this.spotOnBlid && SPOTON_BLID_PATTERN.test(this.spotOnBlid)
    },
    spotOnBlidInputValid() {
      return SPOTON_BLID_PATTERN.test(this.spotOnBlidInput)
    },
    isFormValid() {
      if (this.isSpotOnApiProvider && !(this.hasValidSpotOnBlid || this.spotOnBlidInputValid))
        return false
      return this.integration
        ? (this.integration.inputs || []).every(
            ({ name }) => name in this.credentials || name in this.dynamicFields
          )
        : false
    },
    isConnectedModel() {
      return this.connectedIntegration && !this.connectedIntegration?.disabled
    },
    hasIntegrationProvider() {
      return !!this.connectedIntegration?.integrationProvider
    },
    // Registry-backed providers carry the provider registry's _id on this.integration;
    // legacy hardcoded providers (olo, toast, grubhub, ...) never do. Gating hydration on
    // this instead of connectedIntegration.integrationProvider avoids depending on a
    // snapshot field that can be absent even for a genuinely registry-backed connection.
    providerNeedsHydration() {
      return !!this.integration?._id
    },
    // nexus integration/update replaces the stored credentials wholesale, so saving
    // while legacy credentials are still loading would overwrite them with whatever
    // this.credentials holds.
    awaitingLegacyCredentials() {
      return this.legacyHydrationState === 'loading'
    },
    // A provider with an empty/absent inputs schema never gets this.credentials
    // populated by getIntegrationAndSetInputs (it iterates integration.inputs).
    // Used to keep the save payload from sending a credentials value for such a
    // provider, which would otherwise overwrite what's already stored.
    providerHasInputSchema() {
      return !!(this.integration?.inputs && this.integration.inputs.length)
    },
    isSimphonyPreAuth() {
      try {
        return (
          this.integration?.name === 'simphony' &&
          !this.location?.company?.integrationDetails?.simphony?.oauth
        )
      } catch (e) {
        return false
      }
    },
    isSimphonyFormComplete() {
      if (!this.isSimphonyPreAuth) return false
      const required = [
        'oauthHostDomain',
        'stsGen2HostDomain',
        'clientId',
        'orgShortName',
        'username',
        'password'
      ]
      return required.every((k) => (this.credentials[k] || '').toString().trim().length > 0)
    }
  },
  watch: {
    connectedIntegration(val, oldVal) {
      if (!val) return
      if (val.surveyDelay) {
        this.integration.surveyDelay = val.surveyDelay
      }
      if (val.surveyThrottle) {
        this.integration.surveyThrottle = val.surveyThrottle
      }
      if (val.surveyOrderCountThrottle) {
        this.integration.surveyOrderCountThrottle = val.surveyOrderCountThrottle
      }
      if (!oldVal && val && val._id) {
        this.hydrateConnectedIntegrationOnce(val._id)
      }
      if (val.oAuthAccount) {
        this.oAuthName =
          typeof val.oAuthAccount === 'string'
            ? val.oAuthAccount || ''
            : val.oAuthAccount.name || ''
        this.setLocationSelectOptions()
      }
    },
    integration(newIntegration) {
      if (newIntegration) {
        // Only call setLocationSelectOptions if we don't have a connected integration
        // that will trigger getIntegrationAndSetInputs (which also calls setLocationSelectOptions)
        if (!(this.connectedIntegration?._id && this.providerNeedsHydration)) {
          this.setLocationSelectOptions()
        }
      }

      if ('clipboards' in newIntegration) {
        this.integration.clipboards.forEach(
          ({ name, urlWithOrgId = () => {}, urlWithCompanyId }) => {
            const value = this.credentials.isOrg
              ? urlWithOrgId(this.orgId)
              : urlWithCompanyId(this.companyId)
            this.updateCredentials(name, value)
          }
        )
      }
    },
    'credentials.isOrg'() {
      if ('clipboards' in this.integration) {
        this.integration.clipboards.forEach(({ name, urlWithOrgId, urlWithCompanyId }) => {
          const value = this.credentials.isOrg
            ? urlWithOrgId(this.orgId)
            : urlWithCompanyId(this.companyId)
          this.updateCredentials(name, value)
        })
      }
    }
  },
  methods: {
    ...IntegrationModule.mapActions([
      'addIntegration',
      'updateIntegration',
      'getIntegration',
      'getChowNowLocations',
      'getOauthAccountLocations',
      'getQuLocations',
      'getSimphonyLocations',
      'updateIntegrationOauth',
      'setSpotOnBlid'
    ]),
    handleAuthorizeClick() {
      if (this.integration?.name === 'simphony') {
        this.handleSimphonyDirectAuthorize()
      } else {
        this.openOauthAuthorizationLink()
      }
    },
    openOauthAuthorizationLink() {
      if (!this.hasAuthorizationLink) return
      window.parent.postMessage(
        { openUrl: this.connectedIntegration.oauth.authorizationLink },
        Config.baseAppUrl
      )
    },
    async handleSimphonyAuthorize() {
      try {
        this.isLoading = true

        let integrationId = this.createdIntegrationId

        // First check if a Simphony integration already exists for this location
        if (!integrationId) {
          const existingIntegration = this.location.integrations?.find(
            (integration) =>
              integration.provider === 'simphony' ||
              (integration.integrationProvider &&
                this.selectIntegrationProviderById(integration.integrationProvider)?.name ===
                  'simphony')
          )

          if (existingIntegration) {
            integrationId = existingIntegration._id
            this.createdIntegrationId = integrationId
          }
        }

        // Only create integration if we haven't found or created one
        if (!integrationId) {
          const addIntegrationResult = await this.addIntegration({
            type: this.integration.type,
            credentials: this.credentials,
            provider: this.integration.provider,
            locationId: this.location._id,
            companyId: this.location.company._id,
            integrationProviderId: this.integration._id,
            surveyDelay: this.integration.surveyDelay,
            surveyThrottle: this.integration.surveyThrottle,
            surveyOrderCountThrottle: this.integration.surveyOrderCountThrottle,
            ...this.dynamicFields
          })

          if (addIntegrationResult.success === true) {
            integrationId = addIntegrationResult?.data?.data?.data?.integrationId
            if (integrationId) {
              // Store the integration ID for potential retry attempts
              this.createdIntegrationId = integrationId
            }
          } else {
            // Check if error is because integration already exists
            const errorMessage = addIntegrationResult.data || ''
            if (
              errorMessage.toLowerCase().includes('integration already exists') ||
              errorMessage.toLowerCase().includes('already exists')
            ) {
              // Integration exists but we couldn't find it in our check above
              // Try to find it again after the failed create attempt
              const existingIntegration = this.location.integrations?.find(
                (integration) =>
                  integration.provider === 'simphony' ||
                  (integration.integrationProvider &&
                    this.selectIntegrationProviderById(integration.integrationProvider)?.name ===
                      'simphony')
              )

              if (existingIntegration) {
                integrationId = existingIntegration._id
                this.createdIntegrationId = integrationId
              } else {
                // Force refresh location data and try one more time
                this.$root.$emit('getLocationData')
                this.$notify({
                  title: 'Integration exists but could not be found',
                  type: 'error',
                  text: 'Please refresh the page and try again.'
                })
                return
              }
            } else {
              this.$notify({
                title: addIntegrationResult.data,
                type: 'error'
              })
              return
            }
          }
        }

        // Attempt OAuth update
        if (integrationId) {
          const oauthPayload = {
            oauthHostDomain: this.credentials.oauthHostDomain,
            stsGen2HostDomain: this.credentials.stsGen2HostDomain,
            clientId: this.credentials.clientId,
            orgShortName: this.credentials.orgShortName,
            username: this.credentials.username,
            password: this.credentials.password,
            companyId: this.location?.company?._id
          }

          const resp = await this.updateIntegrationOauth({
            integrationProviderName: this.integration?.name,
            oauth: oauthPayload
          })

          if (resp?.data?.data || resp?.body?.data) {
            this.$notify({
              title: 'Connected',
              text: `${this.integration.displayName} was successfully authorized.`
            })
            // Trigger location data refresh to update OAuth status
            this.$root.$emit('getLocationData')
            this.close()
            this.$emit('connected', this.integration.provider)
          } else {
            // OAuth failed - show error but keep modal open for retry
            this.$notify({
              title: 'Authorization Failed',
              type: 'error',
              text: 'Failed to authorize with Simphony. Please check your credentials and try again.'
            })
          }
        }
      } finally {
        this.isLoading = false
      }
    },
    async handleSimphonyDirectAuthorize() {
      try {
        this.isLoading = true
        const oauthPayload = {
          oauthHostDomain: this.credentials.oauthHostDomain,
          stsGen2HostDomain: this.credentials.stsGen2HostDomain,
          clientId: this.credentials.clientId,
          orgShortName: this.credentials.orgShortName,
          username: this.credentials.username,
          password: this.credentials.password,
          companyId: this.location?.company?._id
        }
        const response = await this.updateIntegrationOauth({
          integrationProviderName: this.integration?.name,
          oauth: oauthPayload
        })
        if (response?.data?.data || response?.body?.data) {
          this.$notify({
            title: 'Connected',
            text: `${this.integration.displayName} was successfully authorized.`
          })
          // Trigger location data refresh to update OAuth status
          this.$root.$emit('getLocationData')
          this.close()
          this.$emit('connected', this.integration.provider)
        } else {
          // OAuth failed - show error but keep modal open for retry
          this.$notify({
            title: 'Authorization Failed',
            type: 'error',
            text: 'Failed to authorize with Simphony. Please check your credentials and try again.'
          })
        }
      } finally {
        this.isLoading = false
      }
    },
    getOutputValue(output) {
      if (output.value) {
        return output.value
          .replace('${companyId}', this.location.company._id)
          .replace('${locationId}', this.location._id)
      } else if (output.path) {
        let path = output.path.split('.')

        let val = this
        for (var key of path) {
          val = val[key] || ''
        }

        return val
      }
    },
    selectOauth(value) {
      this.oAuthName = value
      this.setLocationSelectOptions()
    },
    handleAddOauthAccount() {
      localStorage.setItem(`integrationNickname:${this.integration.name}`, this.integrationNickname)
      this.openOauthAuthorizationLink()
    },
    selectLocation(value) {
      this.updateCredentials('externalLocationId', value)

      if (value) {
        const externalLocation = this.externalLocations.find((el) => el._key === value)
        const externalIdField = OAUTH_EXTERNAL_COMPANY_ID_FIELDS[this.integration.name]
        const externalCompanyId = externalIdField ? externalLocation[externalIdField] : null

        if (externalCompanyId) {
          this.updateCredentials('externalCompanyId', externalCompanyId)
        }
      }
    },
    getInputValue(input) {
      if (!input || !input.path) return

      let path = input.path.split('.')

      let val = this
      for (var key of path) {
        if (!val) break
        val = val[key]
      }

      return val
    },
    async setLocationSelectOptions() {
      if (this.isSimphonyPreAuth) {
        this.externalLocations = []
        this.showLocationSelect = false
        return
      }

      // Prevent duplicate calls
      if (this.isLoadingLocations) {
        return
      }

      let locations = []
      this.showLocationSelect = false
      const hasCompanySimphonyOauth = !!this.location?.company?.integrationDetails?.simphony?.oauth
      const canFetchLocations =
        !this.needsOauth || (this.integration?.name === 'simphony' && hasCompanySimphonyOauth)

      if (canFetchLocations) {
        this.isLoadingLocations = true
        try {
          if (this.usesNamedOauthAccounts) {
            try {
              locations = await this.getOauthAccountLocations({
                integrationProviderName: this.integration.name,
                oAuthName: this.oAuthName
              })
            } catch (error) {
              locations = []
            }
            locations = locations.map((location) => {
              location._key = location.id
              location.optionName = `${location.name} - ${location.id}`
              return location
            })
            this.showLocationSelect = true
          } else {
            switch (this.integration.name) {
              case 'chownow-api':
                try {
                  locations = await this.getChowNowLocations()
                } catch (error) {
                  locations = []
                }
                locations = locations.map((location) => {
                  location._key = location.restaurant_id
                  location.optionName = `${location.name} - ${location.restaurant_id}`
                  return location
                })
                this.showLocationSelect = true
                break

              case 'qu':
                locations = await this.getQuLocations()
                locations = locations.map((location) => {
                  location._key = location.id
                  location.optionName = `${location.dba_name} - ${location.id}`
                  return location
                })
                this.showLocationSelect = true
                break

              case 'simphony':
                locations = await this.getSimphonyLocations()
                locations = locations.map((location) => {
                  location._key = location.id
                  location.optionName = `${location.name} - ${location.id}`
                  return location
                })
                this.showLocationSelect = true
                break
            }
          }
        } catch (e) {
          // For Simphony, show input fields again instead of closing modal
          if (this.integration.name === 'simphony') {
            this.$notify({
              type: 'error',
              text: 'Failed to load Simphony locations. Please update your credentials and try again.',
              duration: 5000
            })
            // Clear the company OAuth setting temporarily to trigger input fields
            if (this.location?.company?.integrationDetails?.simphony) {
              this.$set(this.location.company.integrationDetails.simphony, 'oauth', null)
            }
          } else {
            this.$notify({
              type: 'error',
              text: `Something went wrong authenticating with ${this.integration.name}`,
              duration: 5000
            })
            this.close()
          }
        } finally {
          this.isLoadingLocations = false
        }
      }

      this.externalLocations = locations
    },
    async getIntegrationAndSetInputs(integrationId) {
      try {
        const result = await this.getIntegration(integrationId)
        this.connectedIntegration = result.data.data.integration
        this.connectedIntegrationLocation = result.data.data.integrationLocation
        if (this.connectedIntegration?.oauth?.degraded) {
          if (!this.oauthLinkRetried) {
            this.oauthLinkRetried = true
            this.getIntegrationAndSetInputs(integrationId)
            return
          }
          this.oauthLinkDegraded = true
          return
        }
        this.oauthLinkDegraded = false
        this.setHydratedIsOrg(this.connectedIntegration?.credentials)
        try {
          if (this.integration.inputs) {
            for (var input of this.integration.inputs) {
              const value = this.getInputValue(input)
              if (typeof value !== 'boolean') {
                this.updateCredentials(input.name, value == null ? '' : String(value))
              }
              this.updateField(input.name, value)
            }
          }
        } catch (error) {
          this.$notify({
            type: 'error',
            text: `Some ${
              this.integration?.displayName || 'integration'
            } details could not be prefilled. You can enter them manually.`,
            duration: 5000
          })
        }
        // Set up location selection options after loading integration data
        this.setLocationSelectOptions()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: `Failed to load ${
            this.integration?.displayName || 'integration'
          } details. Please try again.`,
          duration: 5000
        })
        this.close()
      } finally {
        this.hydratingIntegrationId = null
      }
    },
    setHydratedIsOrg(credentials) {
      if (credentials) {
        this.$set(this.credentials, 'isOrg', credentials.isOrg)
      }
    },
    async hydrateLegacyCredentials(integrationId) {
      try {
        const result = await this.getIntegration(integrationId)
        if (this.connectedIntegration?._id !== integrationId) return
        const credentials = result.data.data.integration.credentials
        if (!credentials || typeof credentials !== 'object' || Array.isArray(credentials)) {
          throw new Error('Integration response carried no credentials')
        }
        this.$set(this.connectedIntegration, 'credentials', credentials)
        this.setHydratedIsOrg(credentials)
        for (const { name } of this.oldIntegrationInputs || []) {
          if (credentials[name]) {
            this.updateCredentials(name, String(credentials[name]))
          }
        }
        this.legacyHydrationState = 'loaded'
      } catch (error) {
        if (this.connectedIntegration?._id !== integrationId) return
        this.$notify({
          type: 'error',
          text: `Failed to load ${
            this.integration?.displayName || this.integration?.name || 'integration'
          } details. Please try again.`,
          duration: 5000
        })
        this.close()
      }
    },
    hydrateConnectedIntegrationOnce(integrationId) {
      if (!integrationId || !this.providerNeedsHydration) return
      if (this.hydratingIntegrationId === integrationId) return
      this.hydratingIntegrationId = integrationId
      this.getIntegrationAndSetInputs(integrationId)
    },
    retryAuthorizationLink() {
      if (!this.connectedIntegration?._id) return
      this.oauthLinkDegraded = false
      this.getIntegrationAndSetInputs(this.connectedIntegration._id)
    },
    setIntegration(integration) {
      this.integration = integration
      if (integration.inputs && integration.inputs.length) {
        integration.inputs.forEach((input) => {
          this.updateField(input.name)
        })
      }
    },
    setConnectedIntegration(connectedIntegrations) {
      this.legacyHydrationState = 'idle'
      this.connectedIntegration = connectedIntegrations ? { ...connectedIntegrations } : null

      this.hydrateConnectedIntegrationOnce(this.connectedIntegration?._id)
      const legacyId = this.connectedIntegration?._id
      if (
        legacyId &&
        !this.providerNeedsHydration &&
        !this.connectedIntegration.integrationProvider
      ) {
        this.legacyHydrationState = 'loading'
        this.hydrateLegacyCredentials(legacyId)
      }
    },
    getCredentialValue(name) {
      let value = null
      if (this.legacyHydrationState === 'loaded') {
        value = this.credentials[name]
      } else if (!this.isLoading && this.credentials) {
        // While update/create API is in progress, app tries to get older data from
        // connectedIntegration object. Avoid doing that by fetching new data from credentials.
        value = this.credentials[name]
      }
      return value
    },
    updateField(key, value) {
      this.$set(this.dynamicFields, key, value)
    },
    updateCredentials(key, value = '') {
      if (key === 'locationName') {
        this.$set(this.credentials, 'externalLocationName', value.trim())
      }
      this.$set(this.credentials, key, value.trim())
    },
    updateSpotOnBlidInput(value) {
      this.spotOnBlidInput = value.trim()
      this.spotOnBlidError =
        this.spotOnBlidInput && !this.spotOnBlidInputValid ? 'Format is BL-XXXX-XXXX-XXXX.' : null
    },
    async ensureSpotOnBlid() {
      if (!this.isSpotOnApiProvider || this.hasValidSpotOnBlid) return true

      this.verifyingSpotOnBlid = true
      const result = await this.setSpotOnBlid({
        locationId: this.location._id,
        blid: this.spotOnBlidInput
      })
      this.verifyingSpotOnBlid = false

      if (result.success) return true

      this.spotOnBlidError = spotOnBlidErrorMessage(result, this.spotOnBlidInput)
      return false
    },
    async create() {
      if (!(await this.ensureSpotOnBlid())) return
      // ...or create
      const addIntegrationResult = await this.addIntegration({
        type: this.integration.type,
        credentials: this.credentials,
        provider: this.integration.provider,
        locationId: this.location._id,
        companyId: this.location.company._id,
        integrationProviderId: this.integration._id,
        surveyDelay: this.integration.surveyDelay,
        surveyThrottle: this.integration.surveyThrottle,
        surveyOrderCountThrottle: this.integration.surveyOrderCountThrottle,
        ...this.dynamicFields
      })

      if (addIntegrationResult.success === true) {
        this.$notify({
          title: 'Connected',
          text: `${this.integration.name} was added.`
        })

        if (
          this.integration.usesOauth ||
          (this.integration.outputs && this.integration.outputs.length)
        ) {
          this.getIntegrationAndSetInputs(addIntegrationResult.data.data.data.integrationId)
        } else {
          this.close()
        }

        this.$emit('connected', this.integration.provider)
      } else {
        this.$notify({
          title: addIntegrationResult.data,
          type: 'error'
        })
      }
    },
    async update() {
      if (this.awaitingLegacyCredentials) return
      if (!(await this.ensureSpotOnBlid())) return
      this.isLoading = true
      const fields = {
        company: this.location.company._id,
        location: this.location._id,
        // TODO verify if "Location" field name is correct
        Location: this.location._id,
        provider: this.integration.provider,
        integrationProvider: this.integration._id,
        type: this.integration.type,
        ...this.dynamicFields,
        oAuthAccount: this.oAuthName,
        surveyDelay: this.integration.surveyDelay,
        surveyThrottle: this.integration.surveyThrottle,
        surveyOrderCountThrottle: this.integration.surveyOrderCountThrottle
      }
      // Omit credentials entirely for a no-input-schema provider so the server's
      // findByIdAndUpdate $set never touches the stored value (nexus integration/update).
      if (this.providerHasInputSchema) {
        fields.credentials = this.credentials
      }
      const payload = {
        fields,
        integrationId: this.connectedIntegration._id
      }
      // this.credentials has the updated fields of the form where fields has the original
      const updatePayload = {
        ...payload,
        updatedFields: this.credentials
      }
      const response = await this.updateIntegration(updatePayload)

      if (response && response.status === 200) {
        this.$notify({
          title: 'Updated',
          text: `${this.integration.name} was updated.`
        })
      } else {
        this.$notify({
          title: 'Error',
          type: 'error',
          text: `We are unable to update ${this.integration.name}.`
        })
      }

      this.close()
      this.$root.$emit('getLocationData')
      this.isLoading = false
    },
    close() {
      this.credentials = {}
      this.connectedIntegration = null
      this.connectedIntegrationLocation = null
      this.isLoading = false
      this.isLoadingLocations = false
      this.createdIntegrationId = null
      this.hydratingIntegrationId = null
      this.legacyHydrationState = 'idle'
      this.oauthLinkRetried = false
      this.oauthLinkDegraded = false
      this.spotOnBlidInput = ''
      this.spotOnBlidError = null
      this.verifyingSpotOnBlid = false
      // Trigger location data refresh to restore any temporarily modified data
      this.$root.$emit('getLocationData')
      this.$refs.modal.hide()
    },
    onCopyToClipboard() {
      this.$notify({
        title: 'Copied to clipboard!'
      })
    },
    validatePositiveInteger(field) {
      if (
        !this.integration[field] ||
        this.integration[field] < 1 ||
        !Number.isInteger(this.integration[field])
      ) {
        this.$set(this.integration, field, 1)
      }
    }
  }
}
</script>
